import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';

const Navbar = ({ currentPage, setPage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navRef = useRef(null);
  const isScrolledRef = useRef(false);

  useEffect(() => {
    const threshold = 10;

    const apply = (nextIsScrolled, { immediate } = { immediate: false }) => {
      const wrapper = wrapperRef.current;
      const nav = navRef.current;
      if (!wrapper || !nav) return;

      if (immediate) {
        gsap.set(wrapper, { paddingTop: nextIsScrolled ? 0 : 12 });
        gsap.set(nav, {
          width: nextIsScrolled ? '100%' : '94%',
          maxWidth: nextIsScrolled ? '100%' : '1200px',
          borderRadius: nextIsScrolled ? 0 : 14,
        });
        return;
      }

      if (nextIsScrolled) {
        gsap.to(wrapper, {
          paddingTop: 0,
          duration: 0.6,
          ease: 'power3.inOut',
          overwrite: 'auto',
        });
        gsap.to(nav, {
          width: '100%',
          maxWidth: '100%',
          borderRadius: 0,
          duration: 0.6,
          ease: 'power3.inOut',
          overwrite: 'auto',
        });
      } else {
        gsap.to(wrapper, {
          paddingTop: 12,
          duration: 0.6,
          ease: 'power3.inOut',
          overwrite: 'auto',
        });
        gsap.to(nav, {
          width: '94%',
          maxWidth: '1200px',
          borderRadius: 14,
          duration: 0.6,
          ease: 'power3.inOut',
          overwrite: 'auto',
        });
      }
    };

    const update = ({ immediate } = { immediate: false }) => {
      const scrollY = window.scrollY;
      const nextIsScrolled = scrollY > threshold;

      if (nextIsScrolled === isScrolledRef.current) return;
      isScrolledRef.current = nextIsScrolled;

      apply(nextIsScrolled, { immediate });
    };

    // Ensure initial state matches current scroll position (no animation on mount).
    isScrolledRef.current = window.scrollY > threshold;
    apply(isScrolledRef.current, { immediate: true });

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isServicesActive = useMemo(() => {
    if (location.pathname === '/services') return true;
    if (location.pathname === '/home') return false;
    return currentPage === 'services';
  }, [location.pathname, currentPage]);

  const goTo = (path) => {
    navigate(path);
    if (typeof setPage === 'function') setPage('home');
    setIsOpen(false);
  };

  const navLinks = [
    { name: 'HOME', action: () => goTo('/home'), active: !isServicesActive && !location.pathname.startsWith('/login') },
    { name: 'SERVICES', action: () => goTo('/services'), active: isServicesActive },
  ];

  return (
    <>
      <div
        ref={wrapperRef}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none pt-3"
      >
        <nav
          ref={navRef}
          style={{ width: '94%', maxWidth: '1200px', borderRadius: '14px' }}
          className="pointer-events-auto bg-[#1A1A1A] px-3 md:px-5 py-2.5 flex justify-between items-center shadow-xl ring-1 ring-slate-400/15 backdrop-blur-md overflow-hidden relative"
        >
          <button onClick={() => goTo('/home')} className="flex items-center gap-3 group shrink-0">
            <div className="relative h-5 md:h-7 w-auto">
              <img src="/navbar%20logo.png" alt="Eclipseer Logo" className="h-full w-auto object-contain" />
            </div>
          </button>

          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={link.action}
                className={`text-sm font-medium tracking-widest transition-colors pb-1 whitespace-nowrap border-b-2 ${link.active ? 'text-white' : 'text-gray-300 hover:text-white'
                  }`}
                style={{
                  borderBottomColor: link.active ? '#9747FF' : 'transparent',
                  color: link.active ? '#fff' : undefined,
                }}
              >
                {link.name}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-6 shrink-0">
            <a
              href="/login"
              className="bg-[#9747FF] hover:bg-[#833de0] text-white px-7 py-2 rounded-xl font-medium transition-all uppercase text-sm tracking-widest shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 hover:-translate-y-1 whitespace-nowrap"
              style={{ color: '#fff' }}
            >
              LOGIN
            </a>
          </div>

          <div className="md:hidden flex items-center gap-4">
            <button onClick={() => setIsOpen((v) => !v)} className="text-white" aria-label="Toggle menu">
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.18 }}
            className="fixed top-20 left-4 right-4 bg-[#1A1A1A] ring-1 ring-slate-400/15 md:hidden flex flex-col gap-6 shadow-2xl z-40 p-6 rounded-2xl pointer-events-auto"
          >
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={link.action}
                className={`text-lg font-medium tracking-wide text-left ${link.active ? 'text-white' : 'text-white/80 hover:text-white'
                  }`}
                style={{ color: link.active ? '#fff' : undefined }}
              >
                {link.name}
              </button>
            ))}
            <a
              href="/login"
              className="bg-[#9747FF] text-white px-6 py-3 rounded-xl font-medium text-center uppercase tracking-wide shadow-lg shadow-purple-900/20"
              style={{ color: '#fff' }}
            >
              LOGIN
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
